// Span resolver — PURE module shared by the game server (require) and the
// WebXR client (esbuild import), exactly like ./lifts.js. Keep it
// dependency-free: both ends answering "what is the ground and the ceiling in
// this column" from the same bytes with the same code is what makes
// client/server divergence inside transit impossible.
//
// spans.bin (world/emit.js writes it, APP_BAKER.md §6) stores per-column air HOLES that
// bores carve out of the implicit solid [-inf, H]:
//   solids = [-inf, f1] u [c1, f2] u ... u [cn, H]
// This module never touches the heightfield and never sees a polyline — the
// chord->column projection happened once, at bake. H stays the caller's
// business (the two ends sample terrain differently by design); the resolver
// only says what the holes do to a column.
'use strict';

const OPEN_SKY = 0xFFFF;
// a standing surface must be within reach of the feet to claim them — the
// same 0.35 m the building collider's step-up grants
const STEP_TOL = 0.35;
// Numeric slack for "are the feet above the crown" in the slab rule —
// quantization noise only, deliberately TINY. A body inside a hole can never
// legitimately carry its feet to the crown at all (the head clamp caps feet
// at crown − eye*0.92 ≈ crown − 1 m every frame), so any feet above it are
// on/in the SOLID over the hole. A generous grace here was measured to
// re-open the knife-edge trap from the other side: a walker climbing out of
// a mouth notch with feet 0.13 m over the fresh crown was classified
// "inside", and the ceiling clamp jammed it down through the roof onto the
// bed (rail at (5979,4378), 2026-08-15).
const CROWN_TOL = 0.05;

class Spans {
  constructor(W, H, res, zBase, zScale) {
    this.W = W; this.H = H; this.res = res;
    this.zBase = zBase; this.zScale = zScale;   // metres per quantum
    // filled by loadSpans: keys (sorted Uint32Array), offs, lens, fq, cq, kind
    this.keys = null; this.offs = null; this.lens = null;
    this.fq = null; this.cq = null; this.kind = null;
    this.bb = [0, -1, 0, -1];
  }

  _bbox() {
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (let i = 0; i < this.keys.length; i++) {
      const gx = this.keys[i] % this.W, gz = (this.keys[i] - gx) / this.W;
      if (gx < x0) x0 = gx; if (gx > x1) x1 = gx;
      if (gz < z0) z0 = gz; if (gz > z1) z1 = gz;
    }
    this.bb = [x0, x1, z0, z1];
  }

  _find(gx, gz) {
    // bbox first: holes cover ~0.1 % of the map, this is the fast path
    if (gx < this.bb[0] || gx > this.bb[1] || gz < this.bb[2] || gz > this.bb[3]) return -1;
    const key = gz * this.W + gx;
    const k = this.keys;
    let lo = 0, hi = k.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (k[mid] < key) lo = mid + 1;
      else if (k[mid] > key) hi = mid - 1;
      else return mid;
    }
    return -1;
  }

  // holes of member column i, ascending by floor: [{floor, crown|null, kind}]
  _col(i) {
    const o = this.offs[i], n = this.lens[i], out = [];
    for (let s = 0; s < n; s++) {
      const cqv = this.cq[o + s];
      out.push({
        floor: this.zBase + this.fq[o + s] * this.zScale,
        crown: cqv === OPEN_SKY ? null : this.zBase + cqv * this.zScale,
        kind: this.kind[o + s],
      });
    }
    return out;
  }

  // raw hole list of grid column (gx, gz), or null when it carries none —
  // the carve rasteriser (client corridor.js) paints coverage from exactly
  // these columns so the drawn hole and the collision hole are one set
  colAt(gx, gz) {
    const i = this._find(gx, gz);
    return i < 0 ? null : this._col(i);
  }

  // lowest hole of the nearest member column, or null — scatter/debug helper
  holeAt(x, z) {
    const i = this._find(Math.floor(x / this.res), Math.floor(z / this.res));
    if (i < 0) return null;
    const h = this._col(i);
    return h.length ? h[0] : null;
  }

  // { ground, ceiling } for a body whose FEET are at feetY, or null when no
  // hole is near — the caller then uses plain terrain. `surfaceY` is the
  // caller's own terrain sample at (x,z): holes only ever REMOVE solid, so
  // they are clipped against it, and an open-sky hole (crown null / above the
  // surface) removes the top solid entirely — the walk surface IS the bed.
  //
  // The column is read as an ordered stack of SOLID INTERVALS
  // ([-inf,f1] u [c1,f2] u ... u [cn,H]) and the answer respects them: a
  // standing surface below the feet is reachable only by falling through AIR.
  // Feet inside a solid stand on that solid's top (the slab rule below) —
  // crossing a crown in either direction is impossible by construction.
  //
  // Floor/crown blend BILINEARLY over the 2x2 member columns around the query
  // (non-member weight re-assigned to members): a 0.5 m stepped bed on a 40 %
  // funicular grade would stutter the walk — terrain is triangle-interpolated
  // and the bed must be too.
  resolve(x, z, feetY, surfaceY) {
    const fx = x / this.res - 0.5, fz = z / this.res - 0.5;
    const gx0 = Math.floor(fx), gz0 = Math.floor(fz);
    const tx = fx - gx0, tz = fz - gz0;
    const w = [(1 - tx) * (1 - tz), tx * (1 - tz), (1 - tx) * tz, tx * tz];
    const cell = [
      this._find(gx0, gz0), this._find(gx0 + 1, gz0),
      this._find(gx0, gz0 + 1), this._find(gx0 + 1, gz0 + 1),
    ];
    let maxN = 0;
    const holes = [null, null, null, null];
    for (let j = 0; j < 4; j++) {
      if (cell[j] < 0) continue;
      holes[j] = this._col(cell[j]);
      if (holes[j].length > maxN) maxN = holes[j].length;
    }
    if (!maxN) return null;

    // blended, surface-clipped holes, ascending (per-column lists are sorted
    // and index-paired across the 2x2 — stable within a run's footprint)
    const hs = [];
    for (let k = 0; k < maxN; k++) {
      let f = 0, c = 0, wSum = 0, open = false;
      for (let j = 0; j < 4; j++) {
        const h = holes[j] && holes[j][k];
        if (!h) continue;
        f += h.floor * w[j];
        wSum += w[j];
        if (h.crown === null) open = true;
        else c += h.crown * w[j];
      }
      if (wSum < 1e-6) continue;
      f /= wSum;
      let crown = open ? null : c / wSum;
      // a crown at/above the local surface caps AT the skin — a paper-thin
      // roof is still a roof (rule 1); only an explicit OPEN_SKY hole is open
      if (crown !== null && crown > surfaceY - 0.05) crown = surfaceY - 0.05;
      if (f >= surfaceY - 0.05) continue;                             // hole entirely above ground
      if (crown !== null && crown - f < 0.05) continue;               // degenerate here
      hs.push({ floor: f, crown });
    }
    if (!hs.length) return null;

    // Standing candidates, ascending: each hole's floor (the top of the solid
    // below it), plus the surface itself when the topmost hole still has a
    // roof. ceiling(candidate) = the crown of the hole directly above it.
    const cands = hs.map((h) => h.floor);
    const topOpen = hs[hs.length - 1].crown === null;
    if (!topOpen) cands.push(surfaceY);

    // the highest surface within step reach of the feet; a body buried below
    // every surface is lifted to the lowest one (never left inside solid)
    let pick = 0;
    for (let i = 0; i < cands.length; i++) {
      if (cands[i] <= feetY + STEP_TOL) pick = i;
    }
    // THE SLAB RULE (SPANS_REV 7's runtime half): solid is solid. Feet clearly
    // ABOVE the picked hole's crown are inside (or on) the SOLID over it — the
    // body stands on that solid's TOP, the next surface up, and is NEVER
    // handed the hole floor below: that would drop it through drawn rock.
    // Measured trap this closes: on a knife-edge embankment (DTM falling
    // 2.7 m within 0.7 m of a rail centerline) a walker's feet dipped under
    // the local skin mid-stride, the step-reach pick fell past the crown to
    // the underpass bed 4 m down, and the walker free-fell through the drawn
    // flank into a dead end (traversal watchdog firing, 2026-08-15 — the
    // failure that twice reverted the rule-9b emit relaxation). One bump
    // suffices: pick was already the highest floor within step reach, so the
    // feet cannot also clear the crown of the hole above it.
    if (pick < hs.length && hs[pick].crown !== null
      && feetY > hs[pick].crown + CROWN_TOL) {
      pick += 1;
    }
    return {
      ground: cands[pick],
      ceiling: pick < hs.length ? hs[pick].crown : null,   // null above the roof / open sky
    };
  }
}

// parse spans.bin — accepts a Node Buffer or an ArrayBuffer.
// A truncated or foreign file is "no spans", never a crash.
function loadSpans(buf) {
  try {
    const dv = buf instanceof ArrayBuffer
      ? new DataView(buf)
      : new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    if (dv.getUint32(0, false) !== 0x53504E31) return null;   // 'SPN1'
    const W = dv.getUint32(4, true);
    const H = dv.getUint32(8, true);
    const res = dv.getFloat32(12, true);
    const zBase = dv.getFloat32(16, true);
    const zScaleCm = dv.getFloat32(20, true);
    const nCols = dv.getUint32(24, true);
    let p = 28, total = 0;
    for (let i = 0; i < nCols; i++) {          // size pass
      const n = dv.getUint8(p + 4);
      total += n;
      p += 5 + n * 5;
    }
    const sp = new Spans(W, H, res, zBase, zScaleCm / 100);
    sp.keys = new Uint32Array(nCols);
    sp.offs = new Uint32Array(nCols);
    sp.lens = new Uint8Array(nCols);
    sp.fq = new Uint16Array(total);
    sp.cq = new Uint16Array(total);
    sp.kind = new Uint8Array(total);
    p = 28;
    let o = 0;
    for (let i = 0; i < nCols; i++) {
      sp.keys[i] = dv.getUint32(p, true);
      const n = dv.getUint8(p + 4);
      p += 5;
      sp.offs[i] = o;
      sp.lens[i] = n;
      for (let s = 0; s < n; s++) {
        sp.fq[o] = dv.getUint16(p, true);
        sp.cq[o] = dv.getUint16(p + 2, true);
        sp.kind[o] = dv.getUint8(p + 4);
        p += 5;
        o++;
      }
    }
    sp._bbox();
    return sp;
  } catch {
    return null;
  }
}

module.exports = { loadSpans, Spans, OPEN_SKY, STEP_TOL, CROWN_TOL };
